import { ExportDocumentFormat } from "types/enums/ExportDocumentFormat.enum";
import { usePDFOpsGenerator } from "./pdfGenerator/usePDFOpsGenerator";
import { useWordOpsDocumentGenerator } from "./wordGenerator/useWordOpsDocumentGenerator";

export const useExportCompanionDocumentFromOps = async (
  documentId: string,
  format: ExportDocumentFormat = ExportDocumentFormat.docx,
  returnToStudent: boolean = false,
  printMode: boolean = false,
  hideScores: boolean = false,
  fileNameOverride: string | undefined = undefined,
  includeCriteriaCheckmarks: boolean = true,
  useGoogleDocsCompatibleChecklistLayout: boolean = false,
  options: { includeChecklist?: boolean; studentDraft?: boolean } = {}
) => {
  const documentStore = useSubmittedDocumentStore(documentId);
  const { revision, submittedDocument } = storeToRefs(documentStore);

  const checklistStore = useDocumentChecklistStore(documentId);
  const {
    useChecklist,
    useTotalEarnedScore,
    useTotalScore,
    useAllChecklistCriteria,
  } = storeToRefs(checklistStore);

  const paragraphStore = useDocumentRevisionParagraphsStore(documentId);
  const { wordChecklistCells } = storeToRefs(paragraphStore);

  const annotationStore = useDocumentAnnotationsStore(documentId);
  const { allAnnotations: annotations } = storeToRefs(annotationStore);

  const appUserData = useAppUserDataStore();
  const { commentColor } = storeToRefs(appUserData);

  if (!submittedDocument.value || !revision.value) {
    return;
  }

  if (
    !Array.isArray(revision.value.studentSubmissionOps) ||
    revision.value.studentSubmissionOps.length === 0
  ) {
    useBaseToast("No submission content to export.", "error");
    return;
  }

  let blob: Blob | undefined = undefined;
  // Finished Companion exports mark every criterion. Drafts reflect only
  // the student's current progress through the checklist.
  const filterCheckmarksByStudentCompletion = options.studentDraft === true;
  const checklist = options.includeChecklist === false ? undefined : useChecklist.value;
  const exportRevision = options.studentDraft
    ? {
        ...revision.value,
        studentSubmissionOps: JSON.parse(JSON.stringify(revision.value.studentSubmissionOps)),
        headers: revision.value.headers
          ? JSON.parse(JSON.stringify(revision.value.headers))
          : undefined,
        commentObject: undefined,
      }
    : revision.value;

  if (format == ExportDocumentFormat.docx) {
    const { exportWordOpsDocument } = useWordOpsDocumentGenerator();
    blob = await exportWordOpsDocument(
      submittedDocument.value,
      exportRevision,
      checklist,
      useAllChecklistCriteria.value,
      wordChecklistCells.value,
      useTotalEarnedScore.value,
      useTotalScore.value,
      submittedDocument.value?.inkColor ?? commentColor.value,
      returnToStudent,
      hideScores,
      fileNameOverride,
      includeCriteriaCheckmarks,
      filterCheckmarksByStudentCompletion,
      useGoogleDocsCompatibleChecklistLayout
    );
  }

  if (format == ExportDocumentFormat.pdf) {
    const { exportPDFOpsDocument } = usePDFOpsGenerator();
    blob = await exportPDFOpsDocument(
      submittedDocument.value,
      exportRevision,
      checklist,
      useAllChecklistCriteria.value,
      wordChecklistCells.value,
      options.studentDraft ? [] : annotations.value,
      useTotalEarnedScore.value,
      useTotalScore.value,
      commentColor.value,
      returnToStudent,
      printMode,
      hideScores,
      fileNameOverride,
      includeCriteriaCheckmarks,
      filterCheckmarksByStudentCompletion
    );
  }

  if (returnToStudent && blob) {
    return blob;
  }
};
